import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Business, BusinessRole, Facility, BusinessUser } from '../api_types';
import { adminApi } from '../api/admin_api';

const Businesses: React.FC = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showAddFacilityModal, setShowAddFacilityModal] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [expandedBusinessId, setExpandedBusinessId] = useState<string | null>(null);
  const [newBusiness, setNewBusiness] = useState({ name: '' });
  const [newUser, setNewUser] = useState<{ email: string; name: string; role: BusinessRole }>({ 
    email: '', 
    name: '', 
    role: 'staff' 
  });
  const [newFacility, setNewFacility] = useState<Omit<Facility, 'id' | 'created_at' | 'updated_at'>>({
    name: '',
    location: '',
    courtCount: 0,
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone_number: '',
    email: '',
    website: '',
    description: '',
    capacity: 0,
    amenities: [],
    images: [],
    business_hours: {},
    pricing: {},
    isPPU: false,
    business_id: ''
  });

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const businessesRef = collection(db, 'businesses');
      const snapshot = await getDocs(businessesRef);
      
      // Fetch all businesses with their users and facilities
      const businessesData = await Promise.all(snapshot.docs.map(async (doc) => {
        const businessData = doc.data();
        
        // Fetch users for this business
        const usersSnapshot = await getDocs(collection(db, `businesses/${doc.id}/business_users`));
        const users = usersSnapshot.docs.map(userDoc => ({
          id: userDoc.id,
          ...userDoc.data()
        })) as BusinessUser[];

        // Fetch facilities for this business
        const facilitiesSnapshot = await getDocs(collection(db, `businesses/${doc.id}/facilities`));
        const facilities = facilitiesSnapshot.docs.map(facilityDoc => ({
          id: facilityDoc.id,
          ...facilityDoc.data()
        })) as Facility[];

        // Sort facilities by name
        const sortedFacilities = facilities.sort((a, b) => {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();
          
          // Split the names into parts (text and numbers)
          const aParts = aName.match(/(\d+|\D+)/g) || [];
          const bParts = bName.match(/(\d+|\D+)/g) || [];
          
          // Compare each part
          for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
            const aPart = aParts[i];
            const bPart = bParts[i];
            
            // If both parts are numbers, compare numerically
            if (/^\d+$/.test(aPart) && /^\d+$/.test(bPart)) {
              const diff = parseInt(aPart) - parseInt(bPart);
              if (diff !== 0) return diff;
            } else {
              // Otherwise compare as strings
              const diff = aPart.localeCompare(bPart);
              if (diff !== 0) return diff;
            }
          }
          
          // If all parts match up to the length of the shorter name,
          // the shorter name comes first
          return aParts.length - bParts.length;
        });

        return {
          id: doc.id,
          ...businessData,
          users,
          facilities: sortedFacilities
        } as Business;
      }));

      // Sort businesses by name
      const sortedBusinesses = businessesData.sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        
        // Split the names into parts (text and numbers)
        const aParts = aName.match(/(\d+|\D+)/g) || [];
        const bParts = bName.match(/(\d+|\D+)/g) || [];
        
        // Compare each part
        for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
          const aPart = aParts[i];
          const bPart = bParts[i];
          
          // If both parts are numbers, compare numerically
          if (/^\d+$/.test(aPart) && /^\d+$/.test(bPart)) {
            const diff = parseInt(aPart) - parseInt(bPart);
            if (diff !== 0) return diff;
          } else {
            // Otherwise compare as strings
            const diff = aPart.localeCompare(bPart);
            if (diff !== 0) return diff;
          }
        }
        
        // If all parts match up to the length of the shorter name,
        // the shorter name comes first
        return aParts.length - bParts.length;
      });

      setBusinesses(sortedBusinesses);
      setError(null);
    } catch (err) {
      setError('Failed to fetch businesses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBusiness = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const businessesRef = collection(db, 'businesses');
      await addDoc(businessesRef, {
        name: newBusiness.name,
        createdAt: new Date().toISOString()
      });
      await fetchBusinesses();
      setShowAddModal(false);
      setNewBusiness({ name: '' });
    } catch (err) {
      setError('Failed to create business');
      console.error(err);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBusiness) return;

    try {
      await adminApi.createBusinessUser(selectedBusiness.id, {
        email: newUser.email,
        name: newUser.name,
        role: newUser.role
      });

      await fetchBusinesses();
      setShowAddUserModal(false);
      setNewUser({ email: '', name: '', role: 'staff' });
    } catch (err) {
      setError('Failed to create user');
      console.error(err);
    }
  };

  const handleDeleteUser = async (businessId: string, userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await adminApi.deleteBusinessUser(businessId, userId);
      await fetchBusinesses();
    } catch (err) {
      setError('Failed to delete user');
      console.error(err);
    }
  };

  const handleAddFacility = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBusiness) return;

    try {
      const facilitiesRef = collection(db, 'businesses', selectedBusiness.id, 'facilities');
      await addDoc(facilitiesRef, {
        ...newFacility,
        business_id: selectedBusiness.id,
        createdAt: new Date().toISOString()
      });
      await fetchBusinesses();
      setShowAddFacilityModal(false);
      setNewFacility({
        name: '',
        location: '',
        courtCount: 0,
        address: '',
        city: '',
        state: '',
        zip_code: '',
        phone_number: '',
        email: '',
        website: '',
        description: '',
        capacity: 0,
        amenities: [],
        images: [],
        business_hours: {},
        pricing: {},
        isPPU: false,
        business_id: ''
      });
    } catch (err) {
      setError('Failed to create facility');
      console.error(err);
    }
  };

  const handleDeleteBusiness = async (businessId: string) => {
    if (!window.confirm('Are you sure you want to delete this business?')) return;
    
    try {
      await deleteDoc(doc(db, 'businesses', businessId));
      await fetchBusinesses();
    } catch (err) {
      setError('Failed to delete business');
      console.error(err);
    }
  };

  const handleDeleteFacility = async (businessId: string, facilityId: string) => {
    if (!window.confirm('Are you sure you want to delete this facility?')) return;
    
    try {
      await deleteDoc(doc(db, 'businesses', businessId, 'facilities', facilityId));
      await fetchBusinesses();
    } catch (err) {
      setError('Failed to delete facility');
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <h1 className="text-xl font-bold text-gray-900">Business Management</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-indigo-600 hover:text-indigo-900"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Businesses</h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
            >
              Add New Business
            </button>
          </div>

          {loading ? (
            <div className="text-center py-4">Loading businesses...</div>
          ) : (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Facilities</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {businesses.map((business) => (
                    <React.Fragment key={business.id}>
                      <tr 
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedBusinessId(expandedBusinessId === business.id ? null : business.id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {business.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {business.users?.length || 0} users
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBusiness(business);
                              setShowAddUserModal(true);
                            }}
                            className="ml-3 inline-block bg-indigo-50 text-indigo-700 px-3 py-1 rounded hover:bg-indigo-100 font-medium text-xs transition"
                          >
                            Add User
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {business.facilities?.length || 0} facilities
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBusiness(business);
                              setShowAddFacilityModal(true);
                            }}
                            className="ml-3 inline-block bg-green-50 text-green-700 px-3 py-1 rounded hover:bg-green-100 font-medium text-xs transition"
                          >
                            Add Facility
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteBusiness(business.id);
                            }}
                            className="text-red-600 hover:text-red-800 font-semibold transition"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                      {expandedBusinessId === business.id && (
                        <tr>
                          <td colSpan={4} className="px-0 py-0 bg-gray-50">
                            <div className="p-6 rounded-b-lg bg-gray-50 border-t border-gray-200">
                              {/* Business Users Section */}
                              <div className="mb-8">
                                <h4 className="text-base font-semibold text-gray-800 mb-3">Business Users</h4>
                                {business.users && business.users.length > 0 ? (
                                  <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                                    <table className="min-w-full divide-y divide-gray-200">
                                      <thead className="bg-gray-100">
                                        <tr>
                                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Role</th>
                                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-100">
                                        {business.users.map((user) => (
                                          <tr key={user.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 text-sm text-gray-900">{user.name}</td>
                                            <td className="px-4 py-2 text-sm text-gray-500">{user.email}</td>
                                            <td className="px-4 py-2 text-sm text-gray-500">{user.role}</td>
                                            <td className="px-4 py-2 text-sm">
                                              <button
                                                onClick={() => handleDeleteUser(business.id, user.id)}
                                                className="text-red-600 hover:text-red-800 font-semibold transition"
                                              >
                                                Delete
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500">No users found</p>
                                )}
                              </div>

                              {/* Facilities Section */}
                              <div>
                                <h4 className="text-base font-semibold text-gray-800 mb-3">Facilities</h4>
                                {business.facilities && business.facilities.length > 0 ? (
                                  <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                                    <table className="min-w-full divide-y divide-gray-200">
                                      <thead className="bg-gray-100">
                                        <tr>
                                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Location</th>
                                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-100">
                                        {business.facilities.map((facility) => (
                                          <tr key={facility.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 text-sm text-gray-900">{facility.name}</td>
                                            <td className="px-4 py-2 text-sm text-gray-500">{facility.location}</td>
                                            <td className="px-4 py-2 text-sm text-gray-500">
                                              {facility.isPPU ? 'Pay Per Use' : 'Subscription'}
                                            </td>
                                            <td className="px-4 py-2 text-sm">
                                              <button
                                                onClick={() => handleDeleteFacility(business.id, facility.id)}
                                                className="text-red-600 hover:text-red-800 font-semibold transition"
                                              >
                                                Delete
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500">No facilities found</p>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Add Business Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Business</h3>
            <form onSubmit={handleAddBusiness} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Business Name</label>
                <input
                  type="text"
                  value={newBusiness.name}
                  onChange={(e) => setNewBusiness({ ...newBusiness, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                >
                  Add Business
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && selectedBusiness && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Add New User to {selectedBusiness.name}</h3>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value as BusinessRole })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="staff">Facility Staff</option>
                  <option value="coach">Coach</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddUserModal(false)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                >
                  Add User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Facility Modal */}
      {showAddFacilityModal && selectedBusiness && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Facility to {selectedBusiness.name}</h3>
            <form onSubmit={handleAddFacility} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Facility Name</label>
                <input
                  type="text"
                  value={newFacility.name}
                  onChange={(e) => setNewFacility({ ...newFacility, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Location</label>
                <input
                  type="text"
                  value={newFacility.location}
                  onChange={(e) => setNewFacility({ ...newFacility, location: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPPU"
                  checked={newFacility.isPPU}
                  onChange={(e) => setNewFacility({ ...newFacility, isPPU: e.target.checked })}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="isPPU" className="ml-2 block text-sm text-gray-900">
                  Pay Per Use Facility
                </label>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddFacilityModal(false)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                >
                  Add Facility
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Businesses; 